import { Button, cn } from "@dune/ui";
import { AnimatePresence, motion } from "framer-motion";
import {
	AlertCircle,
	CheckCircle,
	ChevronDown,
	ChevronUp,
	FileText,
	Loader2,
} from "lucide-react";
import { useState } from "react";

interface FloatingActionBarProps {
	totalDocuments: number;
	analyzedDocuments: number;
	compliantDocuments: number;
	saveIsPending: boolean;
	finalIsPending: boolean;
	onSave: () => void;
	onFinalSubmit: () => void;
	formIsValid: boolean;
	canValidateByCategory?: boolean;
}

export const FloatingActionBar = ({
	totalDocuments,
	analyzedDocuments,
	compliantDocuments,
	saveIsPending,
	finalIsPending,
	onSave,
	onFinalSubmit,
	formIsValid,
	canValidateByCategory = false,
}: FloatingActionBarProps) => {
	const [isExpanded, setIsExpanded] = useState(false);
	const allDocumentsAnalyzed = analyzedDocuments === totalDocuments;
	const allDocumentsCompliant = compliantDocuments === totalDocuments;
	const canValidate =
		(allDocumentsCompliant || canValidateByCategory) && formIsValid;
	const progressPercentage = Math.round(
		(compliantDocuments / totalDocuments) * 100,
	);

	return (
		<AnimatePresence>
			<motion.div
				initial={{ y: 100, opacity: 0 }}
				animate={{ y: 0, opacity: 1 }}
				exit={{ y: 100, opacity: 0 }}
				transition={{ type: "spring", stiffness: 300, damping: 30 }}
				className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50"
			>
				<motion.div className="bg-white/95 backdrop-blur-lg border border-gray-200 rounded-2xl shadow-xl overflow-hidden min-w-[400px]">
					{/* Compact View */}
					<div className="px-6 py-4">
						<div className="flex items-center gap-4 justify-between">
							<div className="flex items-center gap-4 flex-1">
								{/* Toggle Button */}
								<button
									type="button"
									onClick={() => setIsExpanded(!isExpanded)}
									className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
									aria-label={isExpanded ? "Réduire" : "Expandre"}
								>
									{isExpanded ? (
										<ChevronDown className="w-4 h-4 text-gray-600" />
									) : (
										<ChevronUp className="w-4 h-4 text-gray-600" />
									)}
								</button>

								{/* Compact Progress */}
								<div className="flex items-center gap-3">
									<div className="relative w-12 h-12">
										<svg
											className="w-12 h-12 transform -rotate-90"
											aria-label="Progression"
										>
											<circle
												cx="24"
												cy="24"
												r="20"
												stroke="#e5e7eb"
												strokeWidth="4"
												fill="none"
											>
												<title>Arrière-plan</title>
											</circle>
											<circle
												cx="24"
												cy="24"
												r="20"
												stroke={allDocumentsCompliant ? "#10b981" : "#f97316"}
												strokeWidth="4"
												fill="none"
												strokeDasharray={`${(compliantDocuments / totalDocuments) * 125.66} 125.66`}
												className="transition-all duration-700 ease-out"
											>
												<title>Progression</title>
											</circle>
										</svg>
										<div className="absolute inset-0 flex items-center justify-center">
											<span className="text-xs font-semibold text-gray-900">
												{progressPercentage || "-"}%
											</span>
										</div>
									</div>

									{/* Quick Status */}
									<div className="flex flex-col min-w-0">
										<p className="text-sm font-medium text-gray-900 whitespace-nowrap">
											{allDocumentsCompliant
												? "Prêt à valider"
												: allDocumentsAnalyzed
													? "Documents non conformes"
													: "Analyse en cours"}
										</p>
										<p className="text-xs text-gray-500 whitespace-nowrap">
											{compliantDocuments}/{totalDocuments} conformes
										</p>
									</div>
								</div>
							</div>

							{/* Actions */}
							<div className="flex items-center gap-2 flex-shrink-0">
								<Button
									type="button"
									variant="outline"
									size="sm"
									onClick={onSave}
									disabled={saveIsPending || finalIsPending}
									className="min-w-[100px]"
								>
									{saveIsPending ? (
										<Loader2 className="h-3 w-3 animate-spin" />
									) : (
										"Enregistrer"
									)}
								</Button>
								<Button
									type="submit"
									size="sm"
									onClick={onFinalSubmit}
									disabled={!canValidate || finalIsPending || saveIsPending}
									className={cn(
										"min-w-[100px] transition-all duration-300",
										canValidate
											? "bg-green-600 hover:bg-green-700 text-white"
											: "",
									)}
								>
									{finalIsPending ? (
										<Loader2 className="h-3 w-3 animate-spin" />
									) : (
										<>
											{canValidate && <CheckCircle className="mr-1 h-3 w-3" />}
											Valider
										</>
									)}
								</Button>
							</div>
						</div>
					</div>

					{/* Expanded Details */}
					<AnimatePresence>
						{isExpanded && (
							<motion.div
								initial={{ height: 0, opacity: 0 }}
								animate={{ height: "auto", opacity: 1 }}
								exit={{ height: 0, opacity: 0 }}
								transition={{ type: "spring", stiffness: 300, damping: 30 }}
								className="border-t border-gray-200"
							>
								<div className="p-6 pt-4">
									{/* Detailed Status */}
									<div className="flex items-center gap-6 mb-4">
										{/* Documents Status */}
										<div className="flex items-center gap-3">
											<div
												className={cn(
													"w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300",
													allDocumentsAnalyzed
														? "bg-green-100 text-green-600"
														: "bg-orange-100 text-orange-600",
												)}
											>
												{allDocumentsAnalyzed ? (
													<CheckCircle className="w-4 h-4" />
												) : (
													<FileText className="w-4 h-4" />
												)}
											</div>
											<div>
												<p className="text-sm font-medium text-gray-900">
													Documents analysés
												</p>
												<p className="text-xs text-gray-500">
													{analyzedDocuments} sur {totalDocuments}
												</p>
											</div>
										</div>

										{/* Compliance Status */}
										<div className="flex items-center gap-3">
											<div
												className={cn(
													"w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300",
													allDocumentsCompliant
														? "bg-green-100 text-green-600"
														: analyzedDocuments > 0
															? "bg-orange-100 text-orange-600"
															: "bg-gray-100 text-gray-400",
												)}
											>
												{allDocumentsCompliant ? (
													<CheckCircle className="w-4 h-4" />
												) : (
													<AlertCircle className="w-4 h-4" />
												)}
											</div>
											<div>
												<p className="text-sm font-medium text-gray-900">
													Documents conformes
												</p>
												<p className="text-xs text-gray-500">
													{compliantDocuments} sur {totalDocuments}
												</p>
											</div>
										</div>
									</div>

									{/* Message */}
									{!allDocumentsAnalyzed && (
										<p className="text-xs text-orange-600 bg-orange-50 rounded-lg px-3 py-2">
											<AlertCircle className="inline-block w-3 h-3 mr-1" />
											Certains documents n'ont pas encore été analysés
										</p>
									)}
									{allDocumentsAnalyzed && !allDocumentsCompliant && (
										<p className="text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2">
											<AlertCircle className="inline-block w-3 h-3 mr-1" />
											Certains documents ne sont pas conformes
										</p>
									)}
									{allDocumentsCompliant && (
										<p className="text-xs text-green-600 bg-green-50 rounded-lg px-3 py-2">
											<CheckCircle className="inline-block w-3 h-3 mr-1" />
											Tous les documents sont conformes !
										</p>
									)}
								</div>
							</motion.div>
						)}
					</AnimatePresence>
				</motion.div>
			</motion.div>
		</AnimatePresence>
	);
};
